/**
 * Accordion Frontend JavaScript
 * Implements WAI-ARIA Accordion Pattern for accessibility
 * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 */

class NPIAccordion {
	constructor(accordionElement) {
		this.accordion = accordionElement;
		this.iconType = this.accordion.dataset.iconType || 'plus';
		this.allowMultiple = this.accordion.dataset.allowMultiple === 'true';
		
		this.toggles = this.accordion.querySelectorAll('.npi-accordion-item__toggle');
		this.contents = this.accordion.querySelectorAll('.npi-accordion-item__content');
		
		this.init();
	}
	
	init() {
		this.setupContentAreas();
		this.setupEventListeners();
		this.updateIcons();
		this.setupInitialState();
	}
	
	setupContentAreas() {
		// Move content from source areas to proper accordion structure
		this.accordion.querySelectorAll('.npi-accordion-item').forEach(item => {
			const source = item.querySelector('.npi-accordion-item__source');
			const headerContent = item.querySelector('.npi-accordion-item__header-content');
			const panelContent = item.querySelector('.npi-accordion-item__inner');
			
			if (source) {
				const groups = source.querySelectorAll('.wp-block-group');
				
				// Move first group to header
				if (groups[0] && headerContent) {
					headerContent.appendChild(groups[0]);
				}
				
				// Move second group to panel
				if (groups[1] && panelContent) {
					panelContent.appendChild(groups[1]);
				}
				
				// Remove source container
				source.remove();
			}
		});
	}
	
	setupEventListeners() {
		this.toggles.forEach((toggle, index) => {
			// Click events on toggle button
			toggle.addEventListener('click', (e) => {
				e.preventDefault();
				this.toggleAccordionItem(index);
			});
			
			// Click events on header (delegate to toggle button)
			const header = toggle.closest('.npi-accordion-item__header');
			if (header) {
				header.addEventListener('click', (e) => {
					// Only trigger if clicking the header itself, not the button
					if (e.target !== toggle && !toggle.contains(e.target)) {
						e.preventDefault();
						toggle.click();
					}
				});
			}
			
			// Keyboard events
			toggle.addEventListener('keydown', (e) => {
				this.handleKeydown(e, index);
			});
		});
	}
	
	setupInitialState() {
		this.contents.forEach((content, index) => {
			const toggle = this.toggles[index];
			const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
			
			if (isExpanded) {
				content.style.display = 'block';
				content.removeAttribute('hidden');
			} else {
				content.style.display = 'none';
				content.setAttribute('hidden', '');
			}
		});
	}
	
	toggleAccordionItem(index) {
		const toggle = this.toggles[index];
		const content = this.contents[index];
		const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
		
		if (!this.allowMultiple && !isExpanded) {
			// Close all other items if multiple not allowed
			this.closeAllItems();
		}
		
		if (isExpanded) {
			this.closeItem(index);
		} else {
			this.openItem(index);
		}
		
		this.updateIcons();
	}
	
	openItem(index) {
		const toggle = this.toggles[index];
		const content = this.contents[index];
		const item = toggle.closest('.npi-accordion-item');
		
		toggle.setAttribute('aria-expanded', 'true');
		content.removeAttribute('hidden');
		content.style.display = 'block';
		item.classList.add('is-expanded');
		
		// Smooth animation
		content.style.height = 'auto';
		const height = content.offsetHeight;
		content.style.height = '0';
		content.offsetHeight; // Force reflow
		content.style.transition = 'height 0.3s ease';
		content.style.height = height + 'px';
		
		setTimeout(() => {
			content.style.height = 'auto';
			content.style.transition = '';
		}, 300);
	}
	
	closeItem(index) {
		const toggle = this.toggles[index];
		const content = this.contents[index];
		const item = toggle.closest('.npi-accordion-item');
		
		toggle.setAttribute('aria-expanded', 'false');
		item.classList.remove('is-expanded');
		
		// Smooth animation
		const height = content.offsetHeight;
		content.style.height = height + 'px';
		content.offsetHeight; // Force reflow
		content.style.transition = 'height 0.3s ease';
		content.style.height = '0';
		
		setTimeout(() => {
			content.setAttribute('hidden', '');
			content.style.display = 'none';
			content.style.height = '';
			content.style.transition = '';
		}, 300);
	}
	
	closeAllItems() {
		this.toggles.forEach((toggle, index) => {
			if (toggle.getAttribute('aria-expanded') === 'true') {
				this.closeItem(index);
			}
		});
	}
	
	handleKeydown(event, index) {
		const { key } = event;
		const toggle = this.toggles[index];
		
		switch (key) {
			case 'Enter':
			case ' ':
				event.preventDefault();
				this.toggleAccordionItem(index);
				break;
				
			case 'ArrowDown':
				event.preventDefault();
				this.focusNextToggle(index);
				break;
				
			case 'ArrowUp':
				event.preventDefault();
				this.focusPrevToggle(index);
				break;
				
			case 'Home':
				event.preventDefault();
				this.focusFirstToggle();
				break;
				
			case 'End':
				event.preventDefault();
				this.focusLastToggle();
				break;
		}
	}
	
	focusNextToggle(currentIndex) {
		const nextIndex = (currentIndex + 1) % this.toggles.length;
		this.toggles[nextIndex].focus();
	}
	
	focusPrevToggle(currentIndex) {
		const prevIndex = currentIndex === 0 ? this.toggles.length - 1 : currentIndex - 1;
		this.toggles[prevIndex].focus();
	}
	
	focusFirstToggle() {
		this.toggles[0].focus();
	}
	
	focusLastToggle() {
		this.toggles[this.toggles.length - 1].focus();
	}
	
	updateIcons() {
		this.toggles.forEach((toggle) => {
			const icon = toggle.querySelector('.npi-accordion-item__icon');
			const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
			
			if (icon) {
				if (this.iconType === 'arrow') {
					icon.textContent = isExpanded ? '▼' : '▶';
				} else {
					icon.textContent = isExpanded ? '−' : '+';
				}
			}
		});
	}
}

// Initialize accordions when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
	const accordions = document.querySelectorAll('.npi-accordion');
	accordions.forEach(accordion => new NPIAccordion(accordion));
});

// Re-initialize for dynamically added accordions (for block editor)
if (window.wp && window.wp.domReady) {
	wp.domReady(() => {
		const accordions = document.querySelectorAll('.npi-accordion');
		accordions.forEach(accordion => {
			if (!accordion.dataset.initialized) {
				new NPIAccordion(accordion);
				accordion.dataset.initialized = 'true';
			}
		});
	});
}